import { NavLink } from 'react-router-dom';
import { Compass, Map, PlusCircle, User, CalendarClock, Bell, LogIn } from 'lucide-react';
import Logo from '@components/ui/Logo';
import { useAuth } from '@context/AuthContext';
import { useNotifications } from '@context/NotificationContext';

const baseNavItems = [
  { to: '/feed', icon: Compass, label: 'Explore' },
  { to: '/my-activities', icon: CalendarClock, label: 'My Vibes' },
  { to: '/map', icon: Map, label: 'Map' },
  { to: '/create', icon: PlusCircle, label: 'Create' },
  { to: '/messages', icon: Bell, label: 'Notifications', authOnly: true },
  { to: '/profile', icon: User, label: 'Me' },
];

const NavBellBadge = ({ count }) => {
  if (!count || count < 1) return null;
  const label = count > 99 ? '99+' : String(count);
  return (
    <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gradient-to-r from-rose-500 to-red-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-white dark:ring-surface-950">
      {label}
    </span>
  );
};

const BottomNav = () => {
  const { user } = useAuth();
  const { unreadCount } = useNotifications();

  const navItems = (user ? baseNavItems : baseNavItems.filter((item) => !item.authOnly)).map((item) => {
    if (!user && item.to === '/profile') {
      return { ...item, to: '/login', label: 'Log in', icon: LogIn };
    }
    return item;
  });

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed top-0 left-0 bottom-0 z-[999] w-[240px] flex-col border-r border-surface-200/80 bg-white/90 backdrop-blur-xl dark:border-surface-800/60 dark:bg-surface-950/90">
        <div className="flex items-center gap-3 px-6 h-16 border-b border-surface-100 dark:border-surface-800/50">
          <Logo size="md" to="/feed" />
        </div>

        <nav className="flex-1 flex flex-col gap-1 px-3 pt-6">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/profile' || to === '/login'}
              className={({ isActive }) =>
                `relative flex items-center gap-3.5 rounded-xl px-4 py-3 text-[15px] font-semibold transition-all duration-200 ${
                  isActive
                    ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400'
                    : 'text-surface-500 hover:bg-surface-100 hover:text-surface-800 dark:text-surface-400 dark:hover:bg-surface-800/60 dark:hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative inline-flex">
                    <Icon size={20} strokeWidth={isActive ? 2.4 : 1.8} />
                    {to === '/messages' && <NavBellBadge count={unreadCount} />}
                  </span>
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 pb-5 pt-3 border-t border-surface-100 dark:border-surface-800/50">
          <p className="text-[11px] text-surface-400 dark:text-surface-600">&copy; 2026 Yaarvi</p>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-[999] glass-header safe-bottom border-t border-surface-100/80 dark:border-surface-800/50">
        <div className="mx-auto flex max-w-lg items-center justify-around px-0.5 py-1.5">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/profile' || to === '/login'}
              className={({ isActive }) =>
                `flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-2xl px-0.5 py-1 transition-all duration-200 ${
                  isActive
                    ? 'text-brand-500'
                    : 'text-surface-400 hover:text-surface-600 dark:text-surface-500 dark:hover:text-surface-300'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div
                    className={`relative flex h-8 w-8 items-center justify-center rounded-xl transition-all duration-200 ${
                      isActive ? 'bg-brand-500/10 dark:bg-brand-500/15 scale-110' : ''
                    }`}
                  >
                    <Icon size={19} strokeWidth={isActive ? 2.5 : 1.8} />
                    {to === '/messages' && <NavBellBadge count={unreadCount} />}
                  </div>
                  <span
                    className={`max-w-full truncate px-0.5 text-[8px] font-semibold leading-none sm:text-[9px] ${
                      isActive ? 'text-brand-600 dark:text-brand-400' : ''
                    }`}
                  >
                    {label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
